'use client';
import React, { useState } from 'react';
import { createClient } from '../../lib/supabase/client';

interface CartItem {
  mpn: string;
  brand?: string;
  description?: string;
  quantity: number;
}

interface CustomerData {
  name: string;
  company: string;
  phone: string;
  email: string;
}

interface QuoteCartProps {
  initialCart: CartItem[];
}

const WHATSAPP_NUMBER = '9647731885522';

export default function QuoteCart({ initialCart }: QuoteCartProps) {
  const [cart, setCart] = useState<CartItem[]>(initialCart);
  const [customer, setCustomer] = useState<CustomerData>({
    name: '',
    company: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (index: number, value: number) => {
    setCart((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: Math.max(1, value) } : item
      )
    );
  };

  const handleRemoveItem = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0) {
      setError('Your quote cart is empty. Please add items before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const supabase = createClient();

      // Step 1: Insert quote (customer data)
      console.log('[QuoteCart] Inserting quote for customer:', customer.name);
      const { data: quoteData, error: quoteError } = await supabase
        .from('quotes')
        .insert({
          customer_name: customer.name,
          customer_company: customer.company || null,
          customer_phone: customer.phone,
          customer_email: customer.email || null,
          status: 'pending',
        })
        .select('id')
        .single();

      if (quoteError) {
        console.error('[QuoteCart] Quote insert error:', quoteError);
        throw new Error(quoteError.message);
      }

      const quoteId = quoteData.id;
      console.log('[QuoteCart] Quote created with ID:', quoteId);

      // Step 2: Insert quote items
      const itemsToInsert = cart.map((item) => ({
        quote_id: quoteId,
        mpn: item.mpn,
        brand: item.brand || null,
        description: item.description || null,
        quantity: item.quantity,
        notes: null,
      }));

      console.log('[QuoteCart] Inserting', itemsToInsert.length, 'quote items...');
      const { error: itemsError } = await supabase
        .from('quote_items')
        .insert(itemsToInsert);

      if (itemsError) {
        console.error('[QuoteCart] Quote items insert error:', itemsError);
        throw new Error(itemsError.message);
      }

      console.log('[QuoteCart] Quote items inserted successfully.');

      // Step 3: Build WhatsApp message
      const itemLines = cart
        .map(
          (item, i) =>
            `${i + 1}. MPN: ${item.mpn}${item.brand ? ` | Brand: ${item.brand}` : ''}${item.description ? ` | ${item.description}` : ''} | Qty: ${item.quantity}`
        )
        .join('\n');

      const message =
        `🔧 *New Quote Request — PLC-Iraq*\n\n` +
        `*Customer Details:*\n` +
        `• Name: ${customer.name}\n` +
        (customer.company ? `• Company: ${customer.company}\n` : '') +
        `• Phone: ${customer.phone}\n` +
        (customer.email ? `• Email: ${customer.email}\n` : '') +
        `\n*Requested Parts (${cart.length} item${cart.length > 1 ? 's' : ''}):*\n` +
        `${itemLines}\n\n` +
        `📋 Quote ID: ${quoteId}\n` +
        `⏰ Submitted: ${new Date().toLocaleString('en-IQ', { timeZone: 'Asia/Baghdad' })}`;

      console.log('[QuoteCart] Opening WhatsApp with pre-filled message...');

      // Step 4: Open WhatsApp in new tab
      window.open(
        `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
        '_blank'
      );

      // Step 5: Success state
      setIsSuccess(true);
      setCart([]);
    } catch (err: any) {
      console.error('[QuoteCart] Submission failed:', err);
      setError(
        err?.message || 'An unexpected error occurred. Please try again or contact us directly on WhatsApp.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state UI
  if (isSuccess) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto text-center">
        <div className="text-5xl mb-4">✅</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Quote Request Received!</h2>
        <p className="text-gray-700 text-lg leading-relaxed">
          Thank you, <span className="font-semibold text-[#CE1126]">{customer.name}</span>. Our
          engineers will review your request and reply within{' '}
          <span className="font-semibold">2 hours</span>. Please click{' '}
          <span className="font-semibold text-[#007A3D]">&ldquo;Send&rdquo;</span> in the WhatsApp
          window that just opened to notify our sales team immediately.
        </p>
        <div className="mt-6 p-4 bg-[#007A3D]/10 rounded-lg border border-[#007A3D]/20">
          <p className="text-sm text-[#007A3D] font-medium">
            💬 Didn&apos;t see the WhatsApp window?{' '}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#005a2d] transition-colors"
            >
              Click here to open WhatsApp
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-3xl mx-auto">
      {/* Header */}
      <div className="bg-[#121212] px-6 py-4">
        <h2 className="text-xl font-bold text-white">Request a Quote</h2>
        <p className="text-gray-400 text-sm mt-1">
          Submit your parts list and we&apos;ll respond within 2 hours.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Cart Items Summary */}
        <section>
          <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#CE1126] text-white text-xs font-bold">
              {cart.length}
            </span>
            Parts in Your Request
          </h3>

          {cart.length === 0 ? (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
              <p className="text-sm">No items in cart. Add parts to get started.</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-gray-600 font-medium">MPN</th>
                    <th className="text-left px-4 py-2 text-gray-600 font-medium hidden sm:table-cell">
                      Brand
                    </th>
                    <th className="text-left px-4 py-2 text-gray-600 font-medium hidden md:table-cell">
                      Description
                    </th>
                    <th className="text-center px-4 py-2 text-gray-600 font-medium">Qty</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cart.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-gray-900 font-semibold">
                        {item.mpn}
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">
                        {item.brand || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell text-xs">
                        {item.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(index, parseInt(e.target.value) || 1)
                          }
                          className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126]"
                          aria-label={`Quantity for ${item.mpn}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-gray-400 hover:text-[#CE1126] transition-colors text-lg leading-none"
                          aria-label={`Remove ${item.mpn} from cart`}
                          title="Remove item"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Customer Form */}
        <form onSubmit={handleSubmit} noValidate>
          <section>
            <h3 className="text-base font-semibold text-gray-800 mb-3">Your Contact Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name - required */}
              <div>
                <label
                  htmlFor="qc-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Full Name <span className="text-[#CE1126]">*</span>
                </label>
                <input
                  id="qc-name"
                  name="name"
                  type="text"
                  required
                  value={customer.name}
                  onChange={handleCustomerChange}
                  placeholder="e.g. Ahmed Al-Rashid"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126] transition-colors"
                />
              </div>

              {/* Phone - required */}
              <div>
                <label
                  htmlFor="qc-phone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number <span className="text-[#CE1126]">*</span>
                </label>
                <input
                  id="qc-phone"
                  name="phone"
                  type="tel"
                  required
                  value={customer.phone}
                  onChange={handleCustomerChange}
                  placeholder="+964 7XX XXX XXXX"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126] transition-colors"
                />
              </div>

              {/* Company - optional */}
              <div>
                <label
                  htmlFor="qc-company"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Company Name{' '}
                  <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <input
                  id="qc-company"
                  name="company"
                  type="text"
                  value={customer.company}
                  onChange={handleCustomerChange}
                  placeholder="e.g. Al-Basra Petrochemical"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126] transition-colors"
                />
              </div>

              {/* Email - optional */}
              <div>
                <label
                  htmlFor="qc-email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address{' '}
                  <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <input
                  id="qc-email"
                  name="email"
                  type="email"
                  value={customer.email}
                  onChange={handleCustomerChange}
                  placeholder="you@company.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126] transition-colors"
                />
              </div>
            </div>
          </section>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              ⚠️ {error}
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={isSubmitting || cart.length === 0}
              className="w-full bg-[#CE1126] hover:bg-[#a50e1f] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm shadow-sm hover:shadow-md"
              aria-label="Submit quote request via WhatsApp"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Sending Request...
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Submit Quote Request
                </>
              )}
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">
              Your request will be saved and a WhatsApp message will open for you to send.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
